<template>
  <div class="relative inline-block text-left" ref="datePickerContainer">
    <button
      type="button"
      @click="togglePicker"
      class="flex items-center text-sm w-72 bg-gray-50 space-x-0 text-center px-2 py-2.5 border border-gray-300 rounded-lg font-medium text-slate-800 hover:bg-slate-100 transition-colors focus:outline-none focus:ring-1 focus:ring-blue-500"
    >
      <img src="../../assets/img/date.png" class="w-4 h-4 relative left-1 top-0" />
           
      <span class="font-medium w-full text-slate-900">This Month ({{ formattedRangeText }}) </span>
    </button>

    <div
      v-if="isOpen"
      class="absolute right-0 mt-2 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 p-4 w-72"
    >
      <div class="flex items-center justify-between mb-3 px-1">
        <button
          type="button"
          @click="prevMonth"
          class="p-1 rounded-lg hover:bg-gray-100 text-gray-600"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>
        <span class="text-sm font-bold text-gray-800">
          {{ currentMonthName }} {{ currentYear }}
        </span>
        <button
          type="button"
          @click="nextMonth"
          :disabled="isNextMonthDisabled"
          class="p-1 rounded-lg hover:bg-gray-100 text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
      </div>

      <p v-if="errorMessage" class="text-xs text-red-500 mb-2 font-medium text-center">
        {{ errorMessage }}
      </p>

      <div class="grid grid-cols-7 gap-1 text-center mb-1">
        <span v-for="day in dayNames" :key="day" class="text-xs font-semibold text-gray-400 py-1">
          {{ day }}
        </span>
      </div>

      <div class="grid grid-cols-7 gap-1 text-center">
        <div v-for="blank in blankDays" :key="'blank-' + blank" class="h-8"></div>

        <button
          v-for="day in daysInMonth"
          :key="day.dateString"
          type="button"
          :disabled="day.isDisabled"
          @click="handleDateClick(day.date)"
          class="h-8 w-8 mx-auto flex items-center justify-center text-xs rounded-full transition-all"
          :class="getDayClass(day)"
        >
          {{ day.dayNumber }}
        </button>
      </div>

      <div class="flex justify-end space-x-2 mt-4 pt-3 border-t border-gray-100">
        <button
          type="button"
          @click="isOpen = false"
          class="px-3 py-1.5 border text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
        >
          Cancel
        </button>
        <button
          type="button"
          @click="applySelection"
          :disabled="!tempStartDate"
          class="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
        >
          Apply
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DateRangePicker',
  props: {
    value: {
      type: Array,
      default: () => [new Date(), new Date()]
    },
    maxDays: {
      type: Number,
      default: 31
    }
  },
  data() {
    const today = new Date();
    return {
      isOpen: false,
      today: today,
      currentViewDate: new Date(today.getFullYear(), today.getMonth(), 1),
      tempStartDate: this.value && this.value[0] ? new Date(this.value[0]) : null,
      tempEndDate: this.value && this.value[1] ? new Date(this.value[1]) : null,
      errorMessage: '',
      dayNames: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
      monthNames: [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ]
    };
  },
  computed: {
    formattedRangeText() {
      if (!this.value || !this.value[0]) return 'Select Date';
      
      const start = new Date(this.value[0]);
      const end = this.value[1] ? new Date(this.value[1]) : start;

      const formatD = (d) => `${d.getDate()} ${this.monthNames[d.getMonth()]} ${String(d.getFullYear()).slice(-2)}`;

      if (start.toDateString() === end.toDateString()) {
        return formatD(start);
      }

      return `${formatD(start)} - ${formatD(end)}`;
    },
    currentMonthName() {
      return this.monthNames[this.currentViewDate.getMonth()];
    },
    currentYear() {
      return this.currentViewDate.getFullYear();
    },
    blankDays() {
      return new Date(
        this.currentViewDate.getFullYear(),
        this.currentViewDate.getMonth(),
        1
      ).getDay();
    },
    daysInMonth() {
      const year = this.currentViewDate.getFullYear();
      const month = this.currentViewDate.getMonth();
      const totalDays = new Date(year, month + 1, 0).getDate();

      const days = [];
      for (let i = 1; i <= totalDays; i++) {
        const d = new Date(year, month, i);
        const isDisabled = d > this.today;

        days.push({
          dayNumber: i,
          date: d,
          dateString: d.toDateString(),
          isDisabled
        });
      }
      return days;
    },
    isNextMonthDisabled() {
      const nextMonthDate = new Date(
        this.currentViewDate.getFullYear(),
        this.currentViewDate.getMonth() + 1,
        1
      );
      return nextMonthDate > this.today;
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    togglePicker() {
      this.isOpen = !this.isOpen;
    },
    handleClickOutside(event) {
      if (this.$refs.datePickerContainer && !this.$refs.datePickerContainer.contains(event.target)) {
        this.isOpen = false;
      }
    },
    prevMonth() {
      this.currentViewDate = new Date(
        this.currentViewDate.getFullYear(),
        this.currentViewDate.getMonth() - 1,
        1
      );
    },
    nextMonth() {
      if (!this.isNextMonthDisabled) {
        this.currentViewDate = new Date(
          this.currentViewDate.getFullYear(),
          this.currentViewDate.getMonth() + 1,
          1
        );
      }
    },
    handleDateClick(date) {
      this.errorMessage = '';

      if (!this.tempStartDate || (this.tempStartDate && this.tempEndDate)) {
        this.tempStartDate = date;
        this.tempEndDate = null;
      } else if (this.tempStartDate && !this.tempEndDate) {
        if (date < this.tempStartDate) {
          this.tempStartDate = date;
        } else if (date.toDateString() === this.tempStartDate.toDateString()) {
          this.tempEndDate = date;
        } else {
          const diffTime = Math.abs(date - this.tempStartDate);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

          if (diffDays > this.maxDays) {
            this.errorMessage = `Max pilih tanggal ${this.maxDays} hari!`;
            return;
          }

          this.tempEndDate = date;
        }
      }
    },
    getDayClass(day) {
      if (day.isDisabled) return 'text-gray-300 cursor-not-allowed';

      const dTime = day.date.getTime();
      const startTime = this.tempStartDate ? this.tempStartDate.getTime() : null;
      const endTime = this.tempEndDate ? this.tempEndDate.getTime() : null;

      if (startTime && dTime === startTime) {
        return 'bg-blue-600 text-white font-bold';
      }
      if (endTime && dTime === endTime) {
        return 'bg-blue-600 text-white font-bold';
      }
      if (startTime && endTime && dTime > startTime && dTime < endTime) {
        return 'bg-blue-100 text-blue-800 rounded-none';
      }

      return 'text-gray-700 hover:bg-gray-100';
    },
    applySelection() {
      if (this.tempStartDate) {
        const finalEndDate = this.tempEndDate || this.tempStartDate;
        this.$emit('input', [this.tempStartDate, finalEndDate]);
        this.$emit('change', {
          startDate: this.tempStartDate,
          endDate: finalEndDate
        });
        this.isOpen = false;
      }
    }
  }
};
</script>